import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Music, Film, Play } from "lucide-react";
import { api, thumbUrl } from "@/lib/api";
import MediaModal from "@/components/home/MediaModal";

const formatSize = (bytes) => (bytes ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : "");

export default function MusicVideos() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/media").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <section id="music" data-testid="music-section" className="relative py-20 lg:py-32 px-4 sm:px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap items-end justify-between gap-6 mb-14"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-blood mb-4">Originals</p>
            <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Music &amp; Videos
            </h2>
          </div>
          <p className="font-mono text-xs uppercase tracking-widest text-gray-500" data-testid="music-count">
            {items.length} drop{items.length === 1 ? "" : "s"}
          </p>
        </motion.div>

        {items.length === 0 ? (
          <p
            data-testid="music-empty"
            className="font-mono text-sm text-gray-500 border border-dashed border-white/15 p-10 text-center"
          >
            Original tracks and visuals drop here soon. Stay locked in.
          </p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((m, i) => (
              <motion.button
                key={m.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: 0.06 * i, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                onClick={() => setActive(m)}
                data-testid={`music-card-${i}`}
                className="group text-left bg-elevate border border-white/10 hover:border-royal/70 p-6 sm:p-8 flex flex-col transition-colors duration-500 clip-frame"
              >
                {m.thumbnail_path && (
                  <div className="relative -mx-6 -mt-6 sm:-mx-8 sm:-mt-8 mb-6 aspect-video overflow-hidden bg-black" data-testid={`music-thumbnail-${i}`}>
                    <img
                      src={thumbUrl(m.thumbnail_path)}
                      alt={m.title}
                      loading="lazy"
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-elevate via-transparent to-transparent" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-14 h-14 flex items-center justify-center bg-royal/90 text-white group-hover:bg-blood group-hover:scale-110 transition-all duration-300">
                        <Play size={20} fill="currentColor" />
                      </span>
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between mb-6">
                  <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-3 py-1 border border-royal/50 text-blue-400">
                    {m.kind === "video" ? <Film size={12} /> : <Music size={12} />} {m.kind}
                  </span>
                  <span className="font-mono text-xs text-gray-500">{formatSize(m.size)}</span>
                </div>
                {!m.thumbnail_path && (
                  <span className="w-12 h-12 flex items-center justify-center bg-royal/20 text-blue-400 group-hover:bg-blood group-hover:text-white transition-colors duration-300 mb-5">
                    <Play size={18} fill="currentColor" />
                  </span>
                )}
                <h3 className="font-display font-bold uppercase text-xl sm:text-2xl leading-tight group-hover:text-blood transition-colors duration-300">
                  {m.title}
                </h3>
                {m.description && (
                  <p className="mt-3 text-sm text-gray-400 leading-relaxed line-clamp-3">{m.description}</p>
                )}
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <MediaModal item={active} onClose={() => setActive(null)} />
    </section>
  );
}
